import React, { useState } from "react";
import { useTranslation } from "@helpers/translate";
import InfoCircleIcon from "@assets/icons/InfoCircleIcon";
import { DirectionSwitch } from "./components/DirectionSwitch/DirectionSwitch";
import { LeverageSlider } from "./components/LeverageSlider/LeverageSlider";
import { LiquidationPrice } from "./components/LiquidationPrice/LiquidationPrice";
import { RiskSettings } from "./components/RiskSettings/RiskSettings";
import { TradeFooter } from "./components/TradeFooter/TradeFooter";
import { WagerSection } from "./components/WagerSection/WagerSection";
import { LEVERAGE_MAX, LEVERAGE_MIN } from "./constants";
import { useTradeForm } from "./hooks/useTradeForm";
import { FeesModal } from "../../TradeModals";
import "./TradeForm.sass";

/**
 * Trade form container: composes hooks and presentational sections.
 * Public props contract matches Controls usage (unchanged).
 */
export const TradeForm = (props) => {
  const { __ } = useTranslation();
  const form = useTradeForm(props);
  const [isFeesOpen, setIsFeesOpen] = useState(false);

  return (
    <div className="trade-form" data-futures-guide="trading-panel">
      <WagerSection
        isLong={form.isLong}
        directionColor={form.directionColor}
        pairMeta={form.pairMeta}
        baseAsset={form.baseAsset}
        displayWager={form.wager.displayWager}
        wagerFieldWidth={form.wager.wagerFieldWidth}
        onWagerInput={form.wager.onWagerInput}
        onWagerBlur={form.wager.onWagerBlur}
        isWagerError={form.wager.isError}
        wagerErrorText={form.wager.errorText}
        onHalf={form.wager.onHalf}
        onDouble={form.wager.onDouble}
        onMax={form.wager.onMax}
        balanceText={form.balanceText}
        leverageDraft={form.leverageField.leverageDraft}
        leverageInputRef={form.leverageField.leverageInputRef}
        onLeverageInput={form.leverageField.onLeverageInput}
        onLeverageFocus={form.leverageField.onLeverageFocus}
        onLeverageBlur={form.leverageField.onLeverageBlur}
        leverageFieldWidth={form.leverageField.leverageFieldWidth}
      />

      <div className="trade-form__leverage" data-futures-guide="leverage">
        <div className="trade-form__leverage-top">
          <p className="trade-form__leverage-title">
            {__("futures.leverage")}
            <InfoCircleIcon size={12} color={form.mutedColor} />
          </p>

          <div className="trade-form__leverage-scale">
            <LeverageSlider
              value={form.leverage}
              onChange={form.leverageField.applyLeverageFromSlider}
            />

            <div className="trade-form__scale">
              <p className="trade-form__scale-label">{LEVERAGE_MIN}x</p>
              <p className="trade-form__scale-label">{LEVERAGE_MAX}x</p>
            </div>
          </div>
        </div>

        <LiquidationPrice liquidationText={form.liquidationText} />
      </div>

      <DirectionSwitch
        isLong={form.isLong}
        onDirectionChange={form.onDirectionChange}
        longColor={form.longColor}
        shortColor={form.shortColor}
        mutedColor={form.mutedColor}
      />

      <RiskSettings
        mode={form.risk.mode}
        saveRisk={form.risk.saveRisk}
        tpDraft={form.risk.tpDraft}
        slDraft={form.risk.slDraft}
        switchMode={form.risk.switchMode}
        toggleSaveRisk={form.risk.toggleSaveRisk}
        onTpChange={form.risk.onTpChange}
        onSlChange={form.risk.onSlChange}
        onTpBlur={form.risk.onTpBlur}
        onSlBlur={form.risk.onSlBlur}
      />

      <TradeFooter
        onPlaceTrade={form.onPlaceTrade}
        isPlacingTrade={form.isPlacingTrade}
        isDisabled={form.isWagerInvalid}
        mutedColor={form.mutedColor}
        onFeesClick={() => setIsFeesOpen(true)}
      />

      <FeesModal
        isOpen={isFeesOpen}
        onClose={() => setIsFeesOpen(false)}
        wager={props.wager}
        leverage={form.leverage}
        pair={props.pair}
      />
    </div>
  );
};
